import { createFormulaReadContext } from './formula-read-context';
import { compilePictureRow } from './picture-row-construction';
import { fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

/** Shared native readers for raw surface columns (including location buckets). */
export const surfaceReaders = createFormulaReadContext({ catalogRows: {}, abilityTypes: [],
    aggregationStrategy: () => 'host_wins', resolveCarrierRef: () => null, resolveInventoryInstance: () => null });
export const surfaceField = surfaceReaders.layerValue;

/** fetch_pictures order, over SELECT rows; compilePictureRow owns URLs and lean fields. */
export function compileSurfacePictures(rows: readonly Row[], owner: string, type: string, cdnUrl?: string): Row[] {
    const matched = rows.filter(row => Number(surfaceField(row, 'picture_deleted') ?? 0) === 0
        && String(surfaceField(row, 'picture_attached_id') ?? '').toUpperCase() === owner.toUpperCase()
        && surfaceField(row, 'picture_attached_item_type') === type);
    matched.sort((a, b) => {
        for (const [index, name] of ['primary_picture', 'picture_thumbnail', 'picture_number', 'picture_i'].entries()) {
            const x = surfaceField(a, name), y = surfaceField(b, name);
            const delta = x == null || y == null ? Number(x != null) - Number(y != null) : Number(x) - Number(y);
            if (delta) return index < 2 ? -delta : delta;
        }
        return 0;
    });
    if (matched.length && typeof cdnUrl !== 'string') throw new FromBaseStageMissing('surface-pictures', 'cdn-url');
    return matched.map(row => compilePictureRow(row, { cdnUrl: cdnUrl ?? '' }) as Row);
}

/** A supplement distinguishes an empty SELECT result from an unheld parent gallery. */
export function surfacePictureRows(own: readonly Row[] | undefined, context: readonly Row[] | undefined): readonly Row[] {
    if (!Array.isArray(own) || !Array.isArray(context)) throw new FromBaseStageMissing('surface-picture-input', 'own-and-related-galleries');
    const rows = new Map<string, Row>();
    for (const row of [...own, ...context]) {
        if (!fromBaseRecord(row)) throw new TypeError('Invalid raw surface picture');
        const id = String(surfaceField(row, 'picture_id'));
        const index = surfaceField(row, 'picture_i');
        const key = index == null ? id : `${id}:${String(index)}`;
        const prior = rows.get(key);
        if (prior && JSON.stringify(prior) !== JSON.stringify(row)) throw new TypeError(`Conflicting surface picture: ${id}`);
        rows.set(key, row);
    }
    return [...rows.values()];
}
